import React from 'react';
import { 
  FiCheckCircle, 
  FiAlertCircle, 
  FiInfo, 
  FiAlertTriangle,
  FiX 
} from 'react-icons/fi';

/**
 * Alert Component
 * Displays contextual messages with different variants
 */
const Alert = ({
  type = 'info',
  title,
  message,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
}) => {
  // Type configurations
  const typeConfig = {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-500',
      icon: FiCheckCircle,
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-500',
      icon: FiAlertCircle,
    },
    warning: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-500',
      icon: FiAlertTriangle,
    },
    info: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-500',
      icon: FiInfo,
    },
  };

  const config = typeConfig[type] || typeConfig.info;
  const IconComponent = icon || config.icon;

  return (
    <div
      className={`
        ${config.bgColor}
        ${config.borderColor}
        border rounded-lg p-4
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      role="alert"
    >
      <div className="flex items-start">
        {/* Icon */}
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <IconComponent className="h-5 w-5" />
        </div>

        {/* Content */}
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${config.textColor} mb-1`}>
              {title}
            </h3>
          )}
          {message && (
            <div className={`text-sm ${config.textColor}`}>
              {message}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <div className="ml-3 flex-shrink-0">
            <button
              type="button"
              className={`
                inline-flex rounded-md p-1.5
                ${config.textColor}
                hover:bg-white/50
                focus:outline-none focus:ring-2 focus:ring-offset-2
                transition-colors
              `}
              onClick={onDismiss}
            >
              <span className="sr-only">Dismiss</span>
              <FiX className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Toast Notification (can be used with a toast library)
 */
export const Toast = ({
  type = 'info',
  message,
  duration = 5000,
  onClose,
}) => {
  React.useEffect(() => {
    if (duration && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeConfig = {
    success: {
      bgColor: 'bg-bukawa-primary',
      icon: FiCheckCircle,
    },
    error: {
      bgColor: 'bg-red-500',
      icon: FiAlertCircle,
    },
    warning: {
      bgColor: 'bg-yellow-500',
      icon: FiAlertTriangle,
    },
    info: {
      bgColor: 'bg-blue-500',
      icon: FiInfo,
    },
  };

  const config = typeConfig[type] || typeConfig.info;
  const IconComponent = config.icon;

  return (
    <div
      className={`
        ${config.bgColor}
        text-white rounded-lg shadow-lg p-4
        flex items-center gap-3
        animate-fade-in-up
      `}
    >
      <IconComponent className="h-5 w-5 flex-shrink-0" />
      <p className="text-sm font-medium flex-1">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors"
        >
          <FiX className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default Alert;